import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MediaImageDto } from '@jordan/types';
import {
  ActionMenu,
  Alert,
  Breadcrumb,
  ImageUpload,
  PageHeader,
  SettingsPanel,
  Spinner,
} from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { mediaSidebar } from './media/mediaSidebar';
import { apiDelete, apiGet, apiPatch, apiPost, assetUrl } from '../lib/api';

const formatSize = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

export function MediaLibraryPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [items, setItems] = useState<MediaImageDto[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    apiGet<MediaImageDto[]>('/api/media').then(setItems).catch(() => setError('Failed to load the library.'));
  }, []);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
      load();
      setMessage(ok);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const upload = (file: File, dataUrl: string) =>
    run(() => apiPost('/api/media', { dataUrl, label: file.name.replace(/\.[^.]+$/, '') }), 'Image uploaded.');

  const rename = (img: MediaImageDto) => {
    const label = window.prompt('Image name', img.label);
    if (label && label.trim() && label.trim() !== img.label) {
      void run(() => apiPatch(`/api/media/${img.id}`, { label: label.trim() }), 'Image renamed.');
    }
  };

  const remove = (img: MediaImageDto) => {
    if (window.confirm(`Delete "${img.label}"? This cannot be undone.`)) {
      void run(() => apiDelete(`/api/media/${img.id}`), 'Image deleted.');
    }
  };

  const copyUrl = (img: MediaImageDto) => {
    void navigator.clipboard?.writeText(`${window.location.origin}${img.url}`);
    setMessage('Image URL copied to clipboard.');
  };

  return (
    <AppChrome secondarySidebar={mediaSidebar(navigate, pathname)}>
      <PageHeader
        title={<>Media Library - <span className="fw-normal">Image Library</span></>}
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Media Library' }, { label: 'Image Library' }]} />}
      />
      <div className="content">
        {message && <Alert variant="success" icon="ph-check-circle" dismissible>{message}</Alert>}
        {error && <Alert variant="danger" icon="ph-warning" dismissible>{error}</Alert>}

        <SettingsPanel title="Upload image" description="Add images and photographs to reuse across the app (avatars, branding, etc.). PNG, JPG, SVG or WebP up to ~8 MB.">
          <div className="py-3 border-top">
            <ImageUpload previewSrc={null} onSelect={(file, dataUrl) => void upload(file, dataUrl)} />
          </div>
        </SettingsPanel>

        <SettingsPanel title="Library" description="All images in the shared library.">
          <div className="py-3 border-top">
            {!items && <Spinner />}
            {items && items.length === 0 && <p className="text-muted mb-0">No images yet. Upload your first above.</p>}
            <div className="row g-3">
              {items?.map((img) => (
                <div className="col-6 col-md-4 col-xl-3" key={img.id}>
                  <div className="card h-100">
                    <img
                      src={assetUrl(img.url)}
                      alt={img.label}
                      className="card-img-top"
                      style={{ height: 150, objectFit: 'cover', background: '#f1f5f9' }}
                    />
                    <div className="card-body p-2 d-flex align-items-start">
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="fw-semibold text-truncate" title={img.label}>{img.label}</div>
                        <div className="text-muted fs-sm">{formatSize(img.size)}</div>
                      </div>
                      <ActionMenu
                        items={[
                          { label: 'Rename', icon: 'ph-pencil', onClick: () => rename(img) },
                          { label: 'Copy URL', icon: 'ph-link', onClick: () => copyUrl(img) },
                          { label: 'Delete', icon: 'ph-trash', danger: true, divider: true, onClick: () => remove(img) },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {busy && <div className="text-muted fs-sm mt-2">Working…</div>}
          </div>
        </SettingsPanel>
      </div>
    </AppChrome>
  );
}
